import {Box, Button, Stack, TextField, Typography} from '@mui/material';
import {useState} from 'react';
import {Question, QuestionStatus} from '@cp/model';
import {env} from '../../../config/env';
import ButtonQuestionStatus from '../../../atoms/ButtonQuestionStatus/ButtonQuestionStatus';

export interface AdminQuestionReviewQuestionStatusUpdateProps {
  question: Question;
  nextStep: QuestionStatus | null;
  updateLoading: boolean;
  isReviewer: boolean;
  onUpdateStatus: (status: QuestionStatus, statusNote?: string) => void;
}

export default function AdminQuestionReviewQuestionStatusUpdate({
  question,
  nextStep,
  updateLoading,
  isReviewer,
  onUpdateStatus,
}: AdminQuestionReviewQuestionStatusUpdateProps) {
  const [statusNote, setStatusNote] = useState<string>('');

  const handleStatusUpdate = (status: QuestionStatus) => {
    onUpdateStatus(status, statusNote.trim() || undefined);
    setStatusNote('');
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{mb: 2, fontWeight: 500}}>
        Update Question Status:
      </Typography>
      <Stack direction="column" gap={2}>
        <TextField
          label="Status Note (Optional)"
          value={statusNote}
          onChange={(e) => setStatusNote(e.target.value)}
          multiline
          rows={3}
          fullWidth
          placeholder="Enter a note about the status change..."
          disabled={updateLoading}
        />
        {!isReviewer && (
          <Stack direction="row" gap={2} flexWrap="wrap">
            {env.question.workflow.map((status) => {
              const isCurrentStatus = question.status === status;
              const isNextStepStatus = nextStep === status;
              return (
                <ButtonQuestionStatus
                  key={status}
                  status={status}
                  isCurrentStatus={isCurrentStatus}
                  isNextStep={isNextStepStatus}
                  loading={updateLoading}
                  onClick={() => handleStatusUpdate(status)}
                />
              );
            })}
          </Stack>
        )}

        {isReviewer && (
          <Stack direction="row" gap={2} flexWrap="wrap">
            <ButtonQuestionStatus
              status={QuestionStatus.REVIEWED}
              isCurrentStatus={question.status === QuestionStatus.REVIEWED}
              isNextStep={nextStep === QuestionStatus.REVIEWED}
              loading={updateLoading}
              onClick={() => handleStatusUpdate(QuestionStatus.REVIEWED)}
            />
          </Stack>
        )}

        <Stack direction="row" gap={2} flexWrap="wrap">
          {[QuestionStatus.ERROR, QuestionStatus.OUT_OF_SCOPE].map((status) => {
            const isCurrentStatus = question.status === status;
            const statusConfig = env.question.status[status];
            return (
              <Button
                key={status}
                variant="outlined"
                onClick={() => handleStatusUpdate(status)}
                disabled={updateLoading || isCurrentStatus}
                sx={{
                  borderColor: statusConfig?.color,
                  color: statusConfig?.color,
                  '&:hover': {
                    borderColor: statusConfig?.color,
                    bgcolor: 'transparent',
                    opacity: 0.7,
                  },
                  '&:disabled': {
                    borderColor: statusConfig?.color,
                    color: statusConfig?.color,
                    opacity: 0.5,
                  },
                }}
              >
                {isCurrentStatus ? `Current: ${statusConfig?.label}` : statusConfig?.label}
              </Button>
            );
          })}
        </Stack>
      </Stack>
    </Box>
  );
}
